import type { MantineTheme } from '@mantine/core';
import { Group, Stack } from '@mantine/core';
import { Button } from '@repo/foundations';
import { useCallback, useRef } from 'react';
import type { ITunnel } from '../../../api';
import { queryClient, tunnelsQueryKeyFactory } from '../../../api';
import { closeAllModals } from '../../../components/ModalManager';
import CidrWhitelistInput, {
	type CidrWhitelistInputRef,
} from './CidrWhitelistInput';
import { processCidrInput } from './cidrUtils';

interface EditCidrWhitelistModalProps {
	tunnel: ITunnel;
	onUpdateTunnel: (params: {
		data: { id: string; whitelist_ip?: string };
	}) => Promise<ITunnel>;
	theme: MantineTheme;
}

function EditCidrWhitelistModal({
	tunnel,
	onUpdateTunnel,
	theme,
}: EditCidrWhitelistModalProps) {
	const cidrInputRef = useRef<CidrWhitelistInputRef>(null);

	const handleSave = useCallback(async () => {
		// Validate using the shared validation
		if (cidrInputRef.current && !cidrInputRef.current.validate()) {
			return;
		}

		// Get the current value from the component
		const cidrValue = cidrInputRef.current?.getValue() || '';
		const whitelistIp = processCidrInput(cidrValue);

		await onUpdateTunnel({
			data: {
				id: tunnel.id,
				whitelist_ip: whitelistIp,
			},
		});

		closeAllModals();
		queryClient.invalidateQueries({
			queryKey: tunnelsQueryKeyFactory.allLists(),
		});
	}, [tunnel.id, onUpdateTunnel]);

	return (
		<Stack spacing={theme.spacing.md} mt={theme.spacing.md}>
			<CidrWhitelistInput
				theme={theme}
				defaultValue={tunnel.whitelist_ip || '0.0.0.0/0'}
				ref={cidrInputRef}
			/>
			<Group
				spacing={theme.spacing.sm}
				sx={{
					justifyContent: 'flex-end',
				}}
			>
				<Button variant="tertiary" onClick={() => closeAllModals()}>
					Cancel
				</Button>
				<Button onClick={handleSave}>Save changes</Button>
			</Group>
		</Stack>
	);
}

export default EditCidrWhitelistModal;
